import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Handshake, ArrowRightLeft, Clock, Zap } from 'lucide-react';

export const ComingSoonBlock = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <section ref={ref} className="relative py-12 sm:py-16 px-6 bg-white overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full opacity-30">
        <div className="absolute top-1/4 right-10 w-64 h-64 bg-gradient-to-br from-secondary-500/20 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-10 w-96 h-96 bg-gradient-to-tr from-brand-400/20 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-5xl mx-auto">
        {/* Coming Soon Badge */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-8"
        >
          <div className="inline-block relative">
            <motion.div
              animate={{
                scale: [1, 1.05, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute inset-0 bg-gradient-to-r from-secondary-500 to-brand-400 rounded-full blur-lg"
            />
          </div>
        </motion.div>

        {/* Main content card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative bg-gradient-to-br from-surface to-white rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl border border-brand-400/20"
        >
          {/* Decorative corner elements */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-secondary-500/10 to-transparent rounded-tr-3xl" />
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-brand-400/10 to-transparent rounded-bl-3xl" />

          <div className="relative">
            {/* Icon */}
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="flex justify-center mb-6"
            >
              <div className="relative">
                <motion.div
                  animate={{
                    rotate: [0, 5, -5, 0],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="absolute inset-0 bg-gradient-to-br from-secondary-500 to-brand-400 rounded-3xl blur-2xl opacity-30"
                />
                <div className="relative bg-gradient-to-br from-secondary-500 to-brand-400 p-5 sm:p-6 md:p-8 rounded-3xl shadow-2xl">
                  <Handshake className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 text-white" strokeWidth={1.5} />
                </div>
              </div>
            </motion.div>

            {/* Title */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-center mb-5"
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-l from-secondary-500 via-brand-400 to-secondary-500 leading-tight mb-3">
                מעבר לכסף:
                <br />
                ברטרייה קהילתית
              </h2>
            </motion.div>

            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="max-w-3xl mx-auto text-center mb-8"
            >
              <p className="text-base sm:text-lg md:text-xl text-brand-700 font-medium leading-relaxed mb-4">
                תארו לעצמכם עולם שבו אתם יכולים להחליף שירותים ומיומנויות עם השכנים שלכם, ללא צורך בכסף
              </p>
              <p className="text-sm sm:text-base md:text-lg text-brand-600 leading-relaxed">
                הברטרייה הקהילתית שלנו תאפשר לכם לשתף את הכישורים שלכם ולקבל בתמורה שירותים מהקהילה המקומית
              </p>
            </motion.div>

            {/* Feature highlights */}
            <div className="grid md:grid-cols-2 gap-5 mb-8">
              {[
                {
                  icon: ArrowRightLeft,
                  title: 'החלפת שירותים',
                  description: 'תנו ותקבלו ללא כסף',
                },
                {
                  icon: Zap,
                  title: 'חיזוק הקהילה',
                  description: 'בנו קשרים אמיתיים',
                },
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index === 0 ? -30 : 30 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                  className="bg-white rounded-2xl p-4 sm:p-5 md:p-6 shadow-lg border border-brand-400/10 hover:shadow-xl transition-shadow group"
                >
                  <div className="flex items-start gap-4">
                    <div className="relative flex-shrink-0">
                      <div className="absolute inset-0 bg-gradient-to-br from-brand-400 to-secondary-500 rounded-xl blur-md opacity-20 group-hover:opacity-40 transition-opacity" />
                      <div className="relative bg-gradient-to-br from-brand-400 to-secondary-500 p-3 rounded-xl">
                        <feature.icon className="w-6 h-6 text-white" strokeWidth={2} />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-lg sm:text-lg md:text-xl font-bold text-brand-700 mb-2">
                        {feature.title}
                      </h4>
                      <p className="text-sm sm:text-base text-brand-600">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Additional decorative elements */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 1, delay: 1 }}
          className="mt-12 flex justify-center gap-4"
        >
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                y: [0, -10, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.3,
                ease: "easeInOut"
              }}
              className="w-3 h-3 bg-gradient-to-br from-brand-400 to-secondary-500 rounded-full"
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};
